import { useMemo, type FC } from "react";

import { AiSuggestion } from "@app/shared/components/ai/AiSuggestion";
import { cn } from "@design-system/Utilities";

import { questionnairesKeys } from "@app/screens/ExternalQuestionnaires/data";
import { type SupportedLocales } from "@app/shared/utils/locales";
import { FlexCol } from "@design-system/Layout/Flex";
import { type GetQuestionsQuery } from "@generated/client/graphql";
import { useMutationState } from "@tanstack/react-query";
import { useQuestionsTabTranslations } from "../i18n";
import { AnswerAiSuggestionHeader } from "./Header";
import { AnswerAiSuggestedResources } from "./SuggestedResources";
import { AnswerAiSuggestionBlock } from "./SuggestionBlock";

export const AnswerAiSuggestion: FC<{
    question: GetQuestionsQuery["questions"][number];
    locale: SupportedLocales;
}> = ({ question, locale }) => {
    const { t, keys } = useQuestionsTabTranslations();

    const aiSuggestion = question?.answer?.aiSuggestion;

    const aiSuggestionMutationState = useMutationState({
        filters: {
            mutationKey: questionnairesKeys.generateAiSuggestion(
                question.questionnaireId ?? undefined,
                question.id,
            ),
        },
        select: (mutation) => mutation.state.status,
    });

    const aiSubmitEditAnswerMutationState = useMutationState({
        filters: {
            predicate: (mutation) => {
                const key = mutation.options.mutationKey;

                if (!key || key.length < 2) return false;

                return (
                    key.includes("upsertAiSuggestion") &&
                    key.includes(question.id)
                );
            },
        },
        select: (mutation) => mutation.state.status,
    });

    const isSuggestPending =
        (!!aiSuggestionMutationState?.length &&
            aiSuggestionMutationState[aiSuggestionMutationState.length - 1] ===
                "pending") ||
        (question?.answer?.aiSuggestion?.isGenerating ?? false);

    const isSubmitPending =
        (!!aiSubmitEditAnswerMutationState?.length &&
            aiSubmitEditAnswerMutationState[
                aiSubmitEditAnswerMutationState.length - 1
            ] === "pending") ||
        (question?.answer?.aiSuggestion?.isGenerating ?? false);

    const isSuggestError = aiSuggestion?.success === false;

    const isAiSuggestionOpen =
        !!aiSuggestion && !isSuggestPending && !isSubmitPending;

    const motivation = useMemo(() => {
        return aiSuggestion?.motivation;
    }, [question?.answer?.aiSuggestion, t]);

    return (
        <AiSuggestion
            isOpen={isAiSuggestionOpen}
            isLoading={isSuggestPending || isSubmitPending}
            isError={isSuggestError}
            className="flex flex-col justify-center align-middle"
        >
            <AnswerAiSuggestionHeader question={question} locale={locale} />

            <AiSuggestion.Body
                className={cn(!question?.answer?.aiSuggestion && "pb-5")}
            >
                <AnswerAiSuggestionBlock question={question} locale={locale} />
                <AnswerAiSuggestedResources
                    question={question}
                    withButton={true}
                />
                {!!motivation && (
                    <FlexCol gap="4">
                        <AiSuggestion.Separator />
                        <AiSuggestion.Block
                            withCopy
                            title={t(keys.ai_suggestion_motivation)}
                            content={motivation ?? ""}
                        />
                    </FlexCol>
                )}
            </AiSuggestion.Body>
        </AiSuggestion>
    );
};
